import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import api from '../services/api';

/**
 * Prévia renderizada pelo servidor — o mesmo código que monta o PDF.
 *
 * @param dados quando traz `idPaciente`, o servidor substitui os marcadores pelos
 *              dados reais daquele paciente; sem ele, usa texto de exemplo
 *              (`[Nome do Paciente]`), que é o certo para a tela de Modelos.
 */
export default function DocumentoPreview({ html, comCabecalho = true, minHeight = 400, dados = null }) {
    const [renderedHtml, setRenderedHtml] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Serializa `dados` para a dependência do efeito: o objeto é recriado a cada
    // render do pai e dispararia a requisição sem o conteúdo ter mudado.
    const dadosChave = dados ? JSON.stringify(dados) : '';

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        // Debounce: na emissão o HTML muda a cada tecla digitada no editor, e sem
        // isso seria uma requisição por tecla.
        const timer = setTimeout(() => {
            api.post('/documentos/preview',
                    { html: html || '', comCabecalho, ...(dados || {}) },
                    { headers: { Accept: 'text/html' }, transformResponse: r => r, skipGlobalError: true })
                .then(res => { if (!cancelled) setRenderedHtml(res.data || ''); })
                .catch(() => { if (!cancelled) setError('Falha ao gerar prévia.'); })
                .finally(() => { if (!cancelled) setLoading(false); });
        }, 350);

        return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [html, comCabecalho, dadosChave]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            bgcolor: '#ECEFF1', p: 3, borderRadius: 1,
            minHeight, maxHeight: 720, overflowY: 'auto',
        }}>
            <Box
                component="iframe"
                title="Prévia do documento"
                srcDoc={renderedHtml}
                sandbox=""
                sx={{
                    width: '100%', maxWidth: 794, mx: 'auto', display: 'block',
                    minHeight: minHeight - 40, border: 'none',
                    bgcolor: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                    borderRadius: 1,
                }}
            />
        </Box>
    );
}
